import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { legacyApi, SMTPEmailConfig } from '../../services/legacyApi';

interface DefaultSettings {
    id: number;
    setting_name: string;
    setting_description: string;
    setting_value: string;
    data_type: string; // 'number' | 'string' | 'percentage'
    category: string; // 'Materials', 'Production', 'Costing', 'System'
    is_active: boolean;
}

const HIDDEN_LEGACY_SETTINGS = new Set([
    'Skilled Manhours %',
    'Unskilled Manhours %',
]);

export function DefaultsSettingsTab() {
    const [settings, setSettings] = useState<DefaultSettings[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [emailConfig, setEmailConfig] = useState<SMTPEmailConfig>({
        host: 'smtp.titan.email',
        port: 587,
        security: 'STARTTLS',
        username: 'sales@hadafcomposites.ae',
        password: '',
        sender_name: 'HADAF COMPOSITES',
        configured: false,
    });
    const [emailSaving, setEmailSaving] = useState(false);
    const [testRecipient, setTestRecipient] = useState('vincydeveloper@gmail.com');
    const [testSending, setTestSending] = useState(false);

    useEffect(() => {
        fetchSettings();
        fetchEmailConfig();
    }, []);

    const persistFallbackSettings = async (fallbackSettings: DefaultSettings[]) => {
        try {
            await fetch(`${API_BASE_URL}/system/settings/defaults`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(
                    fallbackSettings.map(({ setting_name, setting_description, setting_value, data_type, category, is_active }) => ({
                        setting_name,
                        setting_description,
                        setting_value,
                        data_type,
                        category,
                        is_active
                    }))
                )
            });
        } catch (error) {
            console.warn('Failed to auto-seed default settings table:', error);
        }
    };

    const fetchSettings = async () => {
        try {
            setLoading(true);
            console.log('Fetching settings from /system/settings/defaults...');
            // Fetch from backend - adjust endpoint based on your API structure
            const response = await fetch(`${API_BASE_URL}/system/settings/defaults`, {
                headers: { 'Content-Type': 'application/json' }
            });
            console.log('Response status:', response.status);
            if (response.ok) {
                const data = await response.json();
                console.log('Settings fetched from API:', data.length, 'items');
                console.log('First setting from API:', data[0]);

                if (!Array.isArray(data) || data.length === 0) {
                    const fallback = getDefaultSettings();
                    setSettings(fallback);
                    void persistFallbackSettings(fallback);
                    return;
                }

                // Transform API response if needed
                const transformedData = data
                    .map((setting: any) => ({
                    id: setting.id || undefined,
                    setting_name: setting.setting_name,
                    setting_description: setting.setting_description,
                    setting_value: String(setting.setting_value),
                    data_type: setting.data_type,
                    category: setting.category,
                    is_active: setting.is_active !== undefined ? setting.is_active : true
                    }))
                    .filter((setting: DefaultSettings) => !HIDDEN_LEGACY_SETTINGS.has(setting.setting_name));

                console.log('Transformed data:', transformedData.length, 'items');
                console.log('Categories found:', [...new Set(transformedData.map(s => s.category))]);
                setSettings(transformedData);
            } else {
                console.log('API returned non-200 status, using fallback defaults');
                setSettings(getDefaultSettings());
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            setSettings(getDefaultSettings());
        } finally {
            setLoading(false);
        }
    };

    const fetchEmailConfig = async () => {
        try {
            const config = await legacyApi.system.getEmailConfig();
            setEmailConfig(config);
        } catch (error) {
            console.error('Error fetching email config:', error);
        }
    };

    const handleValueChange = (id: number | string, newValue: string) => {
        setSettings(settings.map((s, index) =>
            (s.id === id || index === id) ? { ...s, setting_value: newValue } : s
        ));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setStatus('idle');

            // Save to backend
            const response = await fetch(`${API_BASE_URL}/system/settings/defaults`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            });

            if (response.ok) {
                setStatus('success');
                setMessage('Default settings saved successfully.');
                setEditingId(null);
                setTimeout(() => setStatus('idle'), 3000);
            } else {
                setStatus('error');
                setMessage('Failed to save settings. Please try again.');
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            setStatus('error');
            setMessage('Error saving settings: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    const handleEmailConfigChange = (field: keyof SMTPEmailConfig, newValue: string) => {
        setEmailConfig((prev) => ({
            ...prev,
            [field]: field === 'port' ? Number(newValue || 0) : newValue,
        }));
    };

    const handleSaveEmailConfig = async () => {
        try {
            setEmailSaving(true);
            const saved = await legacyApi.system.updateEmailConfig({
                host: emailConfig.host,
                port: Number(emailConfig.port || 0),
                security: emailConfig.security,
                username: emailConfig.username,
                password: emailConfig.password,
                sender_name: emailConfig.sender_name,
            });
            setEmailConfig(saved);
            setStatus('success');
            setMessage('Email delivery settings saved successfully.');
            setTimeout(() => setStatus('idle'), 3000);
        } catch (error: any) {
            console.error('Error saving email config:', error);
            setStatus('error');
            setMessage(error?.message || 'Failed to save email configuration.');
        } finally {
            setEmailSaving(false);
        }
    };

    const handleSendTestEmail = async () => {
        try {
            setTestSending(true);
            await legacyApi.system.testEmailSend({
                to: testRecipient,
                subject: 'SMTP test email',
                body: 'This is a test email from the Hadaf ERP SMTP configuration.',
            });
            setStatus('success');
            setMessage(`Test email sent to ${testRecipient}.`);
            setTimeout(() => setStatus('idle'), 3000);
        } catch (error: any) {
            console.error('Error sending test email:', error);
            setStatus('error');
            setMessage(error?.message || 'Failed to send test email.');
        } finally {
            setTestSending(false);
        }
    };

    const categories = ['Materials', 'Production', 'Installation', 'Costing', 'System'];

    return (
        <div className="space-y-6">
            {loading && (
                <div className="p-4 rounded-md bg-blue-50 text-blue-900 flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm">Loading settings...</span>
                </div>
            )}

            {status !== 'idle' && (
                <div className={`p-4 rounded-md flex items-start gap-3 ${status === 'success' ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'}`}>
                    {status === 'success' ? <CheckCircle className="w-5 h-5 mt-0.5" /> : <AlertCircle className="w-5 h-5 mt-0.5" />}
                    <div>
                        <h4 className="font-semibold text-sm">{status === 'success' ? 'Success' : 'Error'}</h4>
                        <p className="text-sm mt-1">{message}</p>
                    </div>
                </div>
            )}

            {!loading && (
                <>
                    <div className="text-xs text-slate-500 mb-4">
                        Loaded {settings.length} settings
                        {settings.length > 0 && (
                            <span> • Categories: {[...new Set(settings.map(s => s.category))].join(', ')}</span>
                        )}
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle>Email Delivery</CardTitle>
                            <CardDescription>Configure SMTP settings for sending PDFs directly to customers and suppliers.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">SMTP Host</label>
                                    <input
                                        value={emailConfig.host}
                                        onChange={(e) => handleEmailConfigChange('host', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="smtp.titan.email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">Port</label>
                                    <input
                                        type="number"
                                        value={emailConfig.port || ''}
                                        onChange={(e) => handleEmailConfigChange('port', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="587"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">Security</label>
                                    <select
                                        value={emailConfig.security}
                                        onChange={(e) => handleEmailConfigChange('security', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="STARTTLS">STARTTLS</option>
                                        <option value="SSL/TLS">SSL/TLS</option>
                                        <option value="NONE">None</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">Sender Display Name</label>
                                    <input
                                        value={emailConfig.sender_name}
                                        onChange={(e) => handleEmailConfigChange('sender_name', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="HADAF COMPOSITES"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">SMTP Username</label>
                                    <input
                                        value={emailConfig.username}
                                        onChange={(e) => handleEmailConfigChange('username', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="sales@hadafcomposites.ae"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">SMTP Password</label>
                                    <input
                                        type="password"
                                        value={emailConfig.password}
                                        onChange={(e) => handleEmailConfigChange('password', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={emailConfig.configured ? 'Saved password retained unless changed' : 'Enter SMTP password'}
                                    />
                                    {emailConfig.configured && (
                                        <p className="text-xs text-slate-500">Leave the password unchanged only if you want to keep the saved credential.</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid gap-4 md:grid-cols-[1fr_auto_auto] md:items-end">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">Test Recipient</label>
                                    <input
                                        type="email"
                                        value={testRecipient}
                                        onChange={(e) => setTestRecipient(e.target.value)}
                                        className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="name@example.com"
                                    />
                                </div>
                                <Button variant="outline" onClick={handleSendTestEmail} disabled={testSending || !testRecipient.trim()}>
                                    {testSending ? 'Sending Test...' : 'Send Test Email'}
                                </Button>
                                <Button onClick={handleSaveEmailConfig} disabled={emailSaving} className="bg-blue-600 hover:bg-blue-700">
                                    {emailSaving ? 'Saving Email...' : 'Save Email Settings'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {categories.map(category => {
                        const categorySettings = settings.filter(s => s.category === category);
                        return (
                            <Card key={category}>
                                <CardHeader>
                                    <CardTitle>{category} Defaults</CardTitle>
                                    <CardDescription>Configure default values for {category.toLowerCase()} calculations ({categorySettings.length})</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {categorySettings.length === 0 ? (
                                        <p className="text-sm text-slate-500">No settings found for this category</p>
                                    ) : (
                                        <div className="space-y-4">
                                            {categorySettings.map((setting, index) => (
                                                <div key={`${category}-${index}`} className="flex items-end gap-4 p-3 border rounded-md bg-slate-50">
                                                    <div className="flex-1">
                                                        <label className="text-sm font-medium text-slate-700 block mb-1">
                                                            {setting.setting_name}
                                                        </label>
                                                        <p className="text-xs text-slate-500 mb-2">{setting.setting_description}</p>
                                                        {(setting.data_type === 'text' || setting.setting_name.startsWith('Annexure') || setting.setting_name === 'Validity') ? (
                                                            <textarea
                                                                value={setting.setting_value}
                                                                onChange={(e) => handleValueChange(setting.id || index, e.target.value)}
                                                                disabled={editingId !== null && editingId !== (setting.id || index)}
                                                                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
                                                                placeholder="Enter value"
                                                            />
                                                        ) : (
                                                            <input
                                                                type={setting.data_type === 'number' ? 'number' : 'text'}
                                                                step={setting.data_type === 'percentage' ? '0.01' : undefined}
                                                                value={setting.setting_value}
                                                                onChange={(e) => handleValueChange(setting.id || index, e.target.value)}
                                                                disabled={editingId !== null && editingId !== (setting.id || index)}
                                                                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                placeholder="Enter value"
                                                            />
                                                        )}
                                                        {setting.data_type === 'percentage' && (
                                                            <p className="text-xs text-slate-500 mt-1">Enter as percent (e.g., 5 for 5%)</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}

                    <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={fetchSettings} disabled={loading || saving}>
                            Reset
                        </Button>
                        <Button onClick={handleSave} disabled={loading || saving} className="bg-blue-600 hover:bg-blue-700">
                            {saving ? 'Saving...' : 'Save All Settings'}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}

// Default settings based on legacy system (frm_estimate_calculation form)


function getDefaultSettings(): DefaultSettings[] {
    return [
        // ===== MATERIALS CATEGORY =====
        {
            id: 1,
            setting_name: 'GRP/FRP Material Density',
            setting_description: 'Default density constant for composite material calculations (kg/m^3)',
            setting_value: '2000',
            data_type: 'number',
            category: 'Materials',
            is_active: true
        },

        // ===== PRODUCTION CATEGORY =====
        {
            id: 2,
            setting_name: 'Default Labour Rate',
            setting_description: 'Default hourly labour rate for production (AED/hour)',
            setting_value: '150',
            data_type: 'number',
            category: 'Production',
            is_active: true
        },
        {
            id: 3,
            setting_name: 'Installation Skilled Manhours %',
            setting_description: 'Percentage of installation manhours allocated to skilled labour (0-100)',
            setting_value: '60',
            data_type: 'percentage',
            category: 'Installation',
            is_active: true
        },
        {
            id: 4,
            setting_name: 'Installation Semi-skilled Manhours %',
            setting_description: 'Percentage of installation manhours allocated to semi-skilled labour (0-100)',
            setting_value: '40',
            data_type: 'percentage',
            category: 'Installation',
            is_active: true
        },
        {
            id: 4.1,
            setting_name: 'Installation Non-working Hours / Week',
            setting_description: 'Weekly non-working allowance used in installation labour distribution',
            setting_value: '8',
            data_type: 'number',
            category: 'Installation',
            is_active: true
        },

        // ===== COSTING - DIRECT COSTS =====
        {
            id: 5,
            setting_name: 'Electricity Cost %',
            setting_description: 'Electricity cost as % of direct material+labour',
            setting_value: '6',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 6,
            setting_name: 'Wastage %',
            setting_description: 'Wastage allowance as % of direct material+labour',
            setting_value: '10',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 7,
            setting_name: 'Painting With Labour %',
            setting_description: 'Painting cost with labour as % of direct costs',
            setting_value: '0',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 8,
            setting_name: 'Acid Wash With Labour %',
            setting_description: 'Acid wash cost with labour as % of direct costs',
            setting_value: '0',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 9,
            setting_name: 'Styro Foam %',
            setting_description: 'Styro foam cost as % of direct costs',
            setting_value: '0',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 10,
            setting_name: 'Production Accessories %',
            setting_description: 'Production accessories cost as % of direct costs',
            setting_value: '0',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },

        // ===== COSTING - TOOLING COSTS =====
        {
            id: 11,
            setting_name: 'Tooling Consumable %',
            setting_description: 'Tooling consumables cost as % of subtotal',
            setting_value: '1',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 12,
            setting_name: 'Tooling Jigs & Fixtures %',
            setting_description: 'Tooling jigs and fixtures cost as % of subtotal',
            setting_value: '1',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 13,
            setting_name: 'Tooling Miscellaneous %',
            setting_description: 'Miscellaneous tooling cost as % of subtotal',
            setting_value: '1',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },

        // ===== COSTING - LOGISTICS COSTS =====
        {
            id: 14,
            setting_name: 'Logistics Freight %',
            setting_description: 'Freight cost as % of subtotal',
            setting_value: '0',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 15,
            setting_name: 'Logistics Packing & Loading %',
            setting_description: 'Packing and loading cost as % of subtotal',
            setting_value: '2',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 16,
            setting_name: 'Installation Rate Per KG',
            setting_description: 'Installation cost per kilogram (AED/kg)',
            setting_value: '0',
            data_type: 'number',
            category: 'Costing',
            is_active: true
        },

        // ===== COSTING - ADMINISTRATIVE COSTS =====
        {
            id: 17,
            setting_name: 'Admin Contingency %',
            setting_description: 'Administrative contingency as % of subtotal',
            setting_value: '1',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 18,
            setting_name: 'Admin Warranty %',
            setting_description: 'Warranty cost as % of subtotal',
            setting_value: '5',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 19,
            setting_name: 'Admin Retention %',
            setting_description: 'Retention cost as % of subtotal',
            setting_value: '10',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 20,
            setting_name: 'Admin Banking & Finance %',
            setting_description: 'Banking and finance charges as % of subtotal',
            setting_value: '0',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 21,
            setting_name: 'Admin Other Costs %',
            setting_description: 'Other administrative costs as % of subtotal',
            setting_value: '0',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },

        // ===== COSTING - OVERHEAD & PROFIT =====
        {
            id: 22,
            setting_name: 'Overhead Per SQM',
            setting_description: 'Overhead cost per square meter (AED/m^2)',
            setting_value: '0',
            data_type: 'number',
            category: 'Costing',
            is_active: true
        },
        {
            id: 23,
            setting_name: 'Default Profit Margin %',
            setting_description: 'Default profit margin percentage (0-100)',
            setting_value: '30',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },
        {
            id: 24,
            setting_name: 'Default Discount %',
            setting_description: 'Default discount percentage (0-100)',
            setting_value: '0',
            data_type: 'percentage',
            category: 'Costing',
            is_active: true
        },

        // ===== SYSTEM CATEGORY =====
        {
            id: 25,
            setting_name: 'VAT Rate',
            setting_description: 'VAT percentage for invoices (0-100)',
            setting_value: '0',
            data_type: 'percentage',
            category: 'System',
            is_active: true
        },
        {
            id: 26,
            setting_name: 'Default Currency',
            setting_description: 'Default currency for all transactions',
            setting_value: 'AED',
            data_type: 'string',
            category: 'System',
            is_active: true
        },
        {
            id: 27,
            setting_name: 'Warehouse Name',
            setting_description: 'Default warehouse for inventory tracking',
            setting_value: 'Main Warehouse',
            data_type: 'string',
            category: 'System',
            is_active: true
        },
        {
            id: 28,
            setting_name: 'Estimate Number Format',
            setting_description: 'Format for estimate ID display (e.g., HC/EST/0001)',
            setting_value: 'HC/EST/',
            data_type: 'string',
            category: 'System',
            is_active: true
        },
        {
            id: 29,
            setting_name: 'Validity',
            setting_description: 'Default quotation validity text',
            setting_value: '15 Days from the date of Quotation',
            data_type: 'text',
            category: 'System',
            is_active: true
        },
        {
            id: 30,
            setting_name: 'Annexure A',
            setting_description: 'Default technical annexure text',
            setting_value:
                'Standard Technical Annexure - GRP / FRP Marine Boxes\n' +
                'Description / Specification\n' +
                'Product: GRP / FRP Marine Box with Lid\n' +
                'Gelcoat Type: ISO Grade Gelcoat\n' +
                'Gelcoat Color: RAL3020\n' +
                'Resin Used: General Purpose (GP) Polyester Resin\n' +
                'Body Thickness: Sides & Lid: 4 mm, Bottom: 5 mm\n' +
                'Accessories Included:\n' +
                '- SS 304 Hinges - 2 Nos / Box\n' +
                '- SS 304 Toggle Latches - 2 Nos / Box\n' +
                '- Rubber Gasket - 1 Set / Box',
            data_type: 'text',
            category: 'System',
            is_active: true
        },
        {
            id: 31,
            setting_name: 'Annexure B',
            setting_description: 'Default terms and conditions text',
            setting_value: '',
            data_type: 'text',
            category: 'System',
            is_active: true
        },
        {
            id: 32,
            setting_name: 'Company TRN',
            setting_description: 'Company TRN used in print document headers',
            setting_value: '105028693700003',
            data_type: 'string',
            category: 'System',
            is_active: true
        }
    ];
}


